import {
  Select,
  SelectOption,
  SelectVariant,
  ToolbarFilter,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { useVisitDefinitions } from '@app/domain/visit/visit.hooks'
import type { IApiVisitDefinition } from '@app/state/visits/helpers'

import { FilterCategories, FilterLabels } from './filters'

import type {
  SelectOptionObject,
  ToolbarChip} from '@patternfly/react-core';

interface Props {
  location?: string
  type: string[]
  showToolbarItem: boolean
  onTypeUpdated: (type: string[]) => void
}

function visitDefinitionIdToName(
  definitions: IApiVisitDefinition[],
  id: string,
): string {
  return definitions.find((it) => it.id === id)?.name
}

function visitDefinitionNameToId(
  definitions: IApiVisitDefinition[],
  name: string,
): string {
  return definitions.find((it) => it.name === name)?.id
}

export const TypeToolbarFilter = ({
  location,
  type,
  onTypeUpdated,
  showToolbarItem,
}: Props) => {
  const { definitions } = useVisitDefinitions(location)

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const selectedTypeNames = useMemo(() => {
    if (type.length > 0 && definitions.length > 0) {
      return type?.map((it) => visitDefinitionIdToName(definitions, it))
    } else {
      return []
    }
  }, [definitions, type])

  const typeMenuItems = useMemo(() => {
    return definitions.map((definition) => {
      return <SelectOption key={definition.id} value={definition.name} />
    })
  }, [definitions])

  const onSelect = useCallback(
    (
      event: React.MouseEvent | React.ChangeEvent,
      selection: string | SelectOptionObject,
    ) => {
      if (
        event.target instanceof HTMLInputElement &&
        typeof selection === 'string'
      ) {
        const checked = event.target.checked
        const prevSelections = selectedTypeNames
        const updatedType = checked
          ? [...prevSelections, selection]
          : prevSelections.filter((value) => value !== selection)

        onTypeUpdated(
          updatedType.map((it) => visitDefinitionNameToId(definitions, it)),
        )
      }
    },
    [definitions, onTypeUpdated, selectedTypeNames],
  )

  const onToggle = useCallback((isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
  }, [])

  const onDelete = useCallback(
    (_, value: string | ToolbarChip) => {
      const updatedType = selectedTypeNames.filter((it) => it !== value)
      onTypeUpdated(
        updatedType.map((it) => visitDefinitionNameToId(definitions, it)),
      )
    },
    [selectedTypeNames, onTypeUpdated, definitions],
  )

  return (
    <ToolbarFilter
      chips={selectedTypeNames}
      deleteChip={onDelete}
      categoryName={FilterLabels[FilterCategories.Type]}
      showToolbarItem={showToolbarItem}
    >
      <Select
        variant={SelectVariant.checkbox}
        aria-label={FilterLabels[FilterCategories.Type]}
        onToggle={onToggle}
        onSelect={onSelect}
        selections={selectedTypeNames}
        isOpen={isDropdownOpen}
        placeholderText="Filter by type"
      >
        {typeMenuItems}
      </Select>
    </ToolbarFilter>
  )
}
